import { StatusPill } from "@/lib/status-config";
import type { TaskComment } from "@/api/tasks";
import { MarkdownContent } from "@/components/ui/markdown-content";
import { MessageAttachmentStrip } from "@/components/panels/message-attachment-strip";
import { messageAttachments } from "@/lib/message-attachments";
import { formatDate, translate } from "@/i18n";
import { canvasCommentsMessages } from "@/i18n/messages/canvas-comments";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";
import { FrameChip } from "@/components/comments/frame-chip";

export interface CommentPopoverFrameTask {
  id: string;
  title: string;
  status: string;
  assignedAgentName: string;
  comments: TaskComment[];
}

interface CommentPopoverAgent {
  agent_template?: string | null;
  template_name?: string | null;
}

function resolveFrameTitle(fid: string): string {
  return (
    useFramesStore.getState().framesById[fid]?.title ??
    translate(canvasCommentsMessages, "frameFallback")
  );
}

function resolveFrameType(fid: string): string {
  return useFramesStore.getState().framesById[fid]?.frameType ?? "frame";
}

function handlePanToFrame(fid: string): void {
  useCanvasStore.getState().panToFrame?.(fid);
}

function commentAuthor(c: TaskComment, projectAgents: CommentPopoverAgent[]) {
  if (c.agent_name) return c.agent_name;
  if (!c.agent_id)
    return translate(
      canvasCommentsMessages,
      c.user ? "authorYou" : "authorSystem",
    );
  return (
    projectAgents.find((a) => a.agent_template === c.agent_id)?.template_name ??
    translate(canvasCommentsMessages, "authorAgent")
  );
}

export function CommentPopoverTaskList({
  frameTasks,
  projectAgents,
}: {
  frameTasks: CommentPopoverFrameTask[];
  projectAgents: CommentPopoverAgent[];
}) {
  if (frameTasks.length === 0) return null;
  return (
    <div
      className="scrollbar-none mb-2 max-h-48 min-h-0 shrink overflow-y-auto"
      data-testid="comment-popover-task-list"
    >
      {frameTasks.map((ft) => (
        <div
          key={ft.id}
          className="mb-2 last:mb-0"
          data-testid={`frame-task-${ft.id}`}
        >
          <div className="mb-1 flex items-center gap-1.5">
            <StatusPill status={ft.status} />
            <span className="min-w-0 flex-1 truncate text-[10px] font-medium text-primary">
              {ft.title}
            </span>
          </div>
          {ft.comments.length > 0 && (
            <div className="ml-1 border-l border-glass-stroke-subtle pl-2">
              {ft.comments.map((c) => (
                <div key={c.id} className="mb-1 last:mb-0">
                  <div className="flex items-center gap-1">
                    <span className="text-[8px] font-medium text-secondary">
                      {commentAuthor(c, projectAgents)}
                    </span>
                    <span className="text-[8px] tabular-nums text-muted">
                      {formatDate(c.created_at, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <MarkdownContent
                    content={c.content}
                    className="text-[9px] leading-relaxed text-secondary"
                  />
                  <MessageAttachmentStrip
                    attachments={messageAttachments({ images: c.images })}
                  />
                  {c.deliverable_frame ? (
                    <FrameChip
                      frameId={c.deliverable_frame}
                      title={resolveFrameTitle(c.deliverable_frame)}
                      frameType={resolveFrameType(c.deliverable_frame)}
                      onPan={handlePanToFrame}
                      className="mt-1"
                    />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="mt-1.5 border-t border-glass-stroke-subtle" />
    </div>
  );
}
